<template>
  <PanelCard
    v-if="capabilities.canResetEmailAddress"
    :title="I18n.t('user.reset_email_address.title')"
  >
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-4">
      <p class="tw-m-0 tw-text-sm tw-text-sf-slate-light-2">
        {{ I18n.t('user.reset_email_address.description') }}
      </p>
      <SfButtonDs
        variant="secondary"
        size="sm"
        class="tw-shrink-0"
        data-testid="user-panel-reset-email-address"
        @click="confirming = true"
      >
        {{ I18n.t('user.reset_email_address.button') }}
      </SfButtonDs>
    </div>

    <SfModal
      v-if="confirming"
      size="s"
      data-testid="user-panel-reset-email-address-modal"
      @close="confirming = false"
    >
      <template #title>
        {{ I18n.t('user.reset_email_address.title') }}
      </template>
      <template #body>
        <p class="tw-m-0 tw-text-center tw-text-sm tw-text-sf-slate-dark-1">
          {{ I18n.t('user.reset_email_address.confirm', { name: userName }) }}
        </p>
      </template>
      <template #footer>
        <div class="tw-flex tw-justify-center tw-gap-2">
          <SfButtonDs variant="secondary" @click="confirming = false">
            {{ I18n.t('common.cancel') }}
          </SfButtonDs>
          <SfButtonDs
            variant="danger"
            :loading="store.resettingEmailAddress"
            data-testid="user-panel-reset-email-address-confirm"
            @click="resetEmailAddress"
          >
            {{ I18n.t('user.reset_email_address.button') }}
          </SfButtonDs>
        </div>
      </template>
    </SfModal>
  </PanelCard>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { EMAIL_ADDRESS_TOAST_ID } from '@app/application/user-management/constants'
import { useUserPanelStore } from '@app/application/user-management/stores/user-panel'
import useI18n from '@app/shared/vue/composables/use-I18n'
import usePanelToast from '@app/application/user-management/composables/use-panel-toast'
import PanelCard from '@app/application/user-management/components/panel-card.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import SfModal from '@app/shared/vue/components/sf-modal/sf-modal.vue'

defineOptions({ name: 'EmailAddressSection' })

const I18n = useI18n()
const store = useUserPanelStore()
const raiseToast = usePanelToast()
const { capabilities } = storeToRefs(store)

const confirming = ref(false)

const userName = computed(() => store.user?.name ?? '')

async function resetEmailAddress(): Promise<void> {
  if (store.resettingEmailAddress) return

  const result = await store.resetEmailAddress()

  confirming.value = false

  if (result) {
    raiseToast(result.message, 'success', EMAIL_ADDRESS_TOAST_ID)
  } else {
    raiseToast(
      I18n.t('user.reset_email_address.error'),
      'danger',
      EMAIL_ADDRESS_TOAST_ID
    )
  }
}
</script>
